import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "page-content" }
const _hoisted_2 = { class: "flex flex-wrap gap-3 my-3" }
const _hoisted_3 = { class: "flex align-items-center gap-3 flex-1" }
const _hoisted_4 = {
  key: 2,
  class: "flex align-items-center"
}
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { key: 2 }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = ["textContent"]
const _hoisted_10 = ["href", "textContent"]
const _hoisted_11 = { class: "ml-2" }
const _hoisted_12 = { class: "ml-3" }

import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import InputSwitch from 'primevue/inputswitch';
import Menu from 'primevue/menu';
import { useToast } from 'primevue/usetoast';
import UserCreate from '@/views/Users/components/UserCreate';
import UserListActions from './UserListActions.vue';
import UserReferralWorkflowModal from '../UserReferrals/components/UserReferralWorkflowModal.vue';
import ImportUsersModal from '@/components/UploadModal/ImportUsersModal.vue';
import ImportIEPModal from '@/components/UploadModal/ImportIEPModal.vue';
import ArchiveModal from '@/components/Modal/ArchiveModal';
import { PERMISSIONS, TENANT_SETTINGS } from '@/enum';
import { can, useDateTime, useRouting, isValidForRoleByTenant, useRequestCancel, useTenant } from '@/composables';
import { usePeopleStore, usePeopleTypesStore } from '@/store';
import { StudentService } from '@/services';
import { UserListFilterInterface } from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'UserList',
  setup(__props) {

const $routing = useRouting();
const $tenant = useTenant();
const peopleStore = usePeopleStore();
const peopleTypeStore = usePeopleTypesStore();
const toast = useToast();
const requestCancel = useRequestCancel();
const importUsersModalOpen = ref(false);
const openWorkflow = ref<boolean>(false);

const filtersOverlay = ref();

const onDisplayFilterOverlay = (event: PointerEvent) => filtersOverlay.value.toggle(event);

const importIEPModalOpen = ref(false);

const defaultFilters = {
  archived: false,
  search: '',
  order: '',
  order_by: '',
  page: '',
  per_page: 15,
  person_type_id: '',
};

const displayAddUserModel = ref(false);

const filters = ref<UserListFilterInterface>({} as UserListFilterInterface);

onMounted(() => init());

onBeforeUnmount(() => {
  requestCancel.requestSaverAbortAll();
});

$routing.routeChangeWatcher(async () => {
  if (isUserList.value) {
    if (can([PERMISSIONS.PEOPLE_INDEX])) await peopleStore.getPeople($routing.route.query);
  }
});

const hasFilter = computed<boolean>(() => !!Object.keys($routing.route.query).length);

const isUserList = computed<boolean>(() => $routing.route.name === 'user.list');

const init = async () => {
  if (isUserList.value) {
    defineFilters();
    if (can([PERMISSIONS.PEOPLE_TYPES_INDEX])) await peopleTypeStore.getPeopleTypes();
    if (can([PERMISSIONS.PEOPLE_INDEX])) await peopleStore.getPeople($routing.route.query);
  }
};

const openImportUsersUploader = () => (importUsersModalOpen.value = true);

const openImportIEPUploader = () => (importIEPModalOpen.value = true);

const exportIEP = async () => {
  toast.add({
    severity: 'info',
    summary: 'Exporting IEPs',
    detail: 'Sending an email with a link to download the exported data.',
    life: 3000,
  });

  await StudentService.getStudentIEPExport()
    .then((response) => doSuccess('Email Sent', response.data.message))
    .catch((error) => doFail(error.data.message));
};

const onCancelAdd = () => (importUsersModalOpen.value = false);

const onCancelIEPAdd = () => (importIEPModalOpen.value = false);

const doSuccess = (detail: string, summary: string) => {
  toast.add({
    severity: 'success',
    summary,
    detail,
    life: 5000,
  });
};

const doFail = (detail: string) => {
  toast.add({
    severity: 'error',
    summary: 'Error',
    detail: detail,
    life: 5000,
  });
};

const defineFilters = async () => {
  const query = $routing.route.query;

  filters.value = {
    archived: query.archived === '1',
    search: query.search ? (query.search as string) : '',
    page: query.page ? parseInt(query.page as string) : '',
    per_page: query.per_page ? parseInt(query.per_page as string) : defaultFilters.per_page,
    person_type_id: query.person_type_id ? parseInt(query.person_type_id as string) : '',
  };
};

const onAddUserToggle = () => (displayAddUserModel.value = !displayAddUserModel.value);
const onWorkflowToggle = () => (openWorkflow.value = !openWorkflow.value);
const onArchived = (event) => {
  const archived = event.target.checked;
  filters.value.archived = archived;
  $routing.addQueryParam('archived', filters.value.archived ? '1' : '0');
};

const onClearFilters = () => {
  filters.value = { ...(defaultFilters as UserListFilterInterface) };
  $routing.removeAllQueryParam();
};

const onPeopleTypeChange = () => $routing.addQueryParam('person_type_id', filters.value.person_type_id);

/**
 * Handle the filtering of results based on the provided search terms,
 * given 3 or or more characters have been provided.
 */
const onSearch = (search: string) => {
  const hasSearchTerm = search.length >= 3;

  if (hasSearchTerm) {
    $routing.addQueryParam('complex_search', search);
    filters.value.search = search;
  } else {
    $routing.removeQueryParam('complex_search');
  }
};

const onPage = (event) => {
  filters.value.per_page = event.rows;
  $routing.addQueryParams({ per_page: event.rows, page: event.page + 1 });
};

const onSort = (event) => {
  const order = event.sortOrder === 1 ? 'asc' : 'desc';
  $routing.addQueryParams({ order, order_by: event.sortField });
};

const items = (() => {
  const actions = [];

  if (can([PERMISSIONS.ORGANIZATION_IMPORT_STUDENTS])) {
    actions.push({
      label: 'Import Users',
      command: openImportUsersUploader,
    });
  }

  if (can([PERMISSIONS.STUDENTS_IMPORT_IEP_INFORMATION])) {
    actions.push({
      label: 'Import IEPs',
      command: openImportIEPUploader,
    });
  }

  if (can([PERMISSIONS.STUDENTS_EXPORT_IEP_INFORMATION])) {
    actions.push({
      label: 'Export IEPs',
      command: exportIEP,
    });
  }

  return actions;
})();

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!
  const _component_InputIcon = _resolveComponent("InputIcon")!
  const _component_InputText = _resolveComponent("InputText")!
  const _component_IconField = _resolveComponent("IconField")!
  const _component_Skeleton = _resolveComponent("Skeleton")!
  const _component_Dropdown = _resolveComponent("Dropdown")!
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_Column = _resolveComponent("Column")!
  const _component_RouterLink = _resolveComponent("RouterLink")!
  const _component_DataTable = _resolveComponent("DataTable")!
  const _component_Card = _resolveComponent("Card")!
  const _directive_debounce = _resolveDirective("debounce")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (openWorkflow.value && _unref(can)([_unref(PERMISSIONS).REFERRAL_PROCESS]))
      ? (_openBlock(), _createBlock(UserReferralWorkflowModal, {
          key: 0,
          onUpdate: init,
          onCancel: onWorkflowToggle
        }))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_component_Card, null, {
        content: _withCtx(() => [
          (displayAddUserModel.value && _unref(can)([_unref(PERMISSIONS).PEOPLE_STORE]))
            ? (_openBlock(), _createBlock(_unref(UserCreate), {
                key: 0,
                onCancel: onAddUserToggle
              }))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createVNode(_component_IconField, { "icon-position": "left" }, {
                default: _withCtx(() => [
                  _createVNode(_component_InputIcon, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_Icon, {
                        name: "search",
                        class: "text-lg"
                      })
                    ]),
                    _: 1
                  }),
                  _withDirectives(_createVNode(_component_InputText, {
                    modelValue: filters.value.search,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((filters.value.search) = $event)),
                    class: "w-full",
                    placeholder: "Search by Name"
                  }, null, 8, ["modelValue"]), [
                    [_directive_debounce, onSearch, "600ms"]
                  ])
                ]),
                _: 1
              }),
              (_unref(peopleTypeStore).loading)
                ? (_openBlock(), _createBlock(_component_Skeleton, {
                    key: 0,
                    height: "36px",
                    width: "160px",
                    "border-radius": "4"
                  }))
                : (_openBlock(), _createBlock(_component_Dropdown, {
                    key: 1,
                    id: "users_people_types",
                    modelValue: filters.value.person_type_id,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((filters.value.person_type_id) = $event)),
                    type: "secondary",
                    options: _unref(peopleTypeStore).types,
                    "option-label": "title",
                    "option-value": "id",
                    placeholder: "Filter by Type",
                    onChange: onPeopleTypeChange
                  }, null, 8, ["modelValue", "options"])),
              (_unref(can)([_unref(PERMISSIONS).PEOPLE_ARCHIVE]))
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _createVNode(_unref(InputSwitch), {
                      modelValue: filters.value.archived,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((filters.value.archived) = $event)),
                      class: "mr-2",
                      onChange: onArchived
                    }, null, 8, ["modelValue"]),
                    _cache[4] || (_cache[4] = _createElementVNode("div", { class: "text-color-secondary" }, [
                      _createElementVNode("label", null, "Show Archived")
                    ], -1))
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", null, [
                (hasFilter.value)
                  ? (_openBlock(), _createBlock(_component_AppButton, {
                      key: 0,
                      type: "secondary",
                      text: "Clear Filters",
                      "icon-left": "clear",
                      onClick: onClearFilters
                    }))
                  : _createCommentVNode("", true)
              ])
            ]),
            (_unref(can)([_unref(PERMISSIONS).PEOPLE_STORE]) && _unref($tenant).tenantSettingEnabled(_unref(TENANT_SETTINGS).REFERRALS))
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _createVNode(_component_AppButton, {
                    type: "secondary",
                    text: "Student Add/Referrals",
                    "icon-left": "person_add",
                    onClick: onWorkflowToggle
                  })
                ]))
              : _createCommentVNode("", true),
            (_unref(can)([_unref(PERMISSIONS).PEOPLE_STORE]) && !_unref($tenant).tenantSettingEnabled(_unref(TENANT_SETTINGS).REFERRALS))
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  _createVNode(_component_AppButton, {
                    type: "secondary",
                    text: "Import/Export",
                    "icon-left": "import_export",
                    "icon-right": "filter_list",
                    onClick: onDisplayFilterOverlay
                  }),
                  _createVNode(_unref(Menu), {
                    ref_key: "filtersOverlay",
                    ref: filtersOverlay,
                    model: _unref(items),
                    popup: true
                  }, null, 8, ["model"])
                ]))
              : _createCommentVNode("", true),
            (_unref(can)([_unref(PERMISSIONS).PEOPLE_STORE]))
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                  _createVNode(_component_AppButton, {
                    type: "primary",
                    text: "Add User",
                    "icon-left": "add",
                    onClick: onAddUserToggle
                  })
                ]))
              : _createCommentVNode("", true)
          ]),
          (_unref(can)([_unref(PERMISSIONS).PEOPLE_INDEX]))
            ? (_openBlock(), _createBlock(_component_DataTable, {
                key: 1,
                filters: filters.value,
                "onUpdate:filters": _cache[3] || (_cache[3] = ($event: any) => ((filters).value = $event)),
                "filter-display": "menu",
                value: _unref(peopleStore).people.data,
                paginator: true,
                class: _normalizeClass(["p-datatable-customers", { 'table-loading': _unref(peopleStore).people.loading }]),
                rows: filters.value.per_page,
                "total-records": _unref(peopleStore).people.total,
                "row-class": ({ archived_at }) => (archived_at ? 'grey-row' : null),
                "data-key": "id",
                "row-hover": true,
                lazy: true,
                "loading-icon": "logo-spinner",
                loading: _unref(peopleStore).people.loading,
                "rows-per-page-options": [15, 25, 30],
                "paginator-template": "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown",
                "current-page-report-template": "Showing {first} to {last} of {totalRecords}",
                "responsive-layout": "scroll",
                onPage: onPage,
                onSort: onSort
              }, {
                empty: _withCtx(() => [
                  (!_unref(peopleStore).people.loading && _unref(peopleStore).people.data.length == 0)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_8, "No users found"))
                    : _createCommentVNode("", true)
                ]),
                default: _withCtx(() => [
                  (_unref(peopleStore).people.data.length)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createVNode(_component_Column, { field: "archived_at" }, {
                          body: _withCtx(({ data }) => [
                            (data.archived_at)
                              ? _withDirectives((_openBlock(), _createBlock(_component_Icon, {
                                  key: 0,
                                  name: "info",
                                  class: "mt-1 text-color-gray-dark text-base cursor-pointer"
                                }, null, 512)), [
                                  [
                                    _directive_tooltip,
                                    {
                    value: 'Archived',
                    escape: true,
                    fitContent: true,
                  },
                                    void 0,
                                    { top: true }
                                  ]
                                ])
                              : _createCommentVNode("", true)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_Column, {
                          field: "full_name",
                          header: "Name",
                          sortable: ""
                        }, {
                          body: _withCtx(({ data }) => [
                            (_unref(isValidForRoleByTenant)(data.people_type))
                              ? (_openBlock(), _createBlock(_component_RouterLink, {
                                  key: 0,
                                  to: `/user/${data.id}`,
                                  class: "font-semibold",
                                  textContent: _toDisplayString(data.full_name)
                                }, null, 8, ["to", "textContent"]))
                              : (_openBlock(), _createElementBlock("span", {
                                  key: 1,
                                  class: "font-semibold",
                                  textContent: _toDisplayString(data.full_name)
                                }, null, 8, _hoisted_9))
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_Column, {
                          field: "email",
                          header: "Email",
                          sortable: true
                        }, {
                          body: _withCtx(({ data }) => [
                            _createElementVNode("a", {
                              href: `mailto:${data.email}`,
                              textContent: _toDisplayString(data.email)
                            }, null, 8, _hoisted_10)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_Column, {
                          field: "people_type.title",
                          header: "Type"
                        }),
                        _createVNode(_component_Column, {
                          field: "location",
                          header: "State"
                        }, {
                          body: _withCtx(({ data }) => [
                            _createElementVNode("span", _hoisted_11, _toDisplayString(data.location?.state?.name ?? 'N/A'), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_Column, {
                          field: "access",
                          header: "Access"
                        }, {
                          body: _withCtx(({ data }) => [
                            _createElementVNode("div", _hoisted_12, [
                              (!!data.user && !data.archived_at)
                                ? (_openBlock(), _createBlock(_component_Icon, {
                                    key: 0,
                                    name: "check",
                                    class: "text-lg text-blue-600"
                                  }))
                                : (_openBlock(), _createBlock(_component_Icon, {
                                    key: 1,
                                    name: "close",
                                    class: "text-lg text-red-600"
                                  }))
                            ])
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_Column, {
                          field: "created_at",
                          header: "Date Created"
                        }, {
                          body: _withCtx(({ data }) => [
                            _createTextVNode(_toDisplayString(_unref(useDateTime)().formatIntlDate(data.created_at)), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_Column, {
                          field: "actions",
                          header: "Actions"
                        }, {
                          body: _withCtx(({ data }) => [
                            _createVNode(UserListActions, {
                              data: data,
                              onOnArchived: init
                            }, null, 8, ["data"])
                          ]),
                          _: 1
                        })
                      ], 64))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }, 8, ["filters", "value", "class", "rows", "total-records", "row-class", "loading"]))
            : _createCommentVNode("", true),
          (importUsersModalOpen.value && _unref(can)([_unref(PERMISSIONS).ORGANIZATION_IMPORT_STUDENTS]))
            ? (_openBlock(), _createBlock(ImportUsersModal, {
                key: 2,
                onCancel: onCancelAdd,
                onSave: doSuccess,
                onError: doFail
              }))
            : _createCommentVNode("", true),
          (importIEPModalOpen.value && _unref(can)([_unref(PERMISSIONS).STUDENTS_IMPORT_IEP_INFORMATION]))
            ? (_openBlock(), _createBlock(ImportIEPModal, {
                key: 3,
                onCancel: onCancelIEPAdd,
                onSave: doSuccess,
                onError: doFail
              }))
            : _createCommentVNode("", true),
          (_unref(can)([_unref(PERMISSIONS).PEOPLE_ARCHIVE]))
            ? (_openBlock(), _createBlock(_unref(ArchiveModal), { key: 4 }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ])
  ], 64))
}
}

})